<script lang="ts">
	import type { Block } from '$models/Block/Block';
	import type { CallToActionBlock as CallToActionBlockType } from '$models/Block/CallToActionBlock';
	import type { ContentBlock as ContentBlockType } from '$models/Block/ContentBlock';
	import type { PanoramaBlock as PanoramaBlockType } from '$models/Block/PanoramaBlock';
	import type { Page } from '$models/Page/Page';
	import type { Component } from 'svelte';
	import CallToActionBlock from '$components/Block/CallToActionBlock.svelte';
	import CardsBlock from '$components/Block/CardsBlock.svelte';
	import ColumnsBlock from '$components/Block/ColumnsBlock.svelte';
	import ContentBlock from '$components/Block/ContentBlock.svelte';
	import ContentCarouselBlock from '$components/Block/ContentCarousel.svelte';
	import DeferredBlock from '$components/Block/DeferredBlock.svelte';
	import FaqBlock from '$components/Block/FaqBlock.svelte';
	import FeaturedBlock from '$components/Block/FeaturedBlock.svelte';
	import FormBlock from '$components/Block/FormBlock.svelte';
	import GridBlock from '$components/Block/GridBlock.svelte';
	import ImmediateBlock from '$components/Block/ImmediateBlock.svelte';
	import ListColumnsBlock from '$components/Block/ListColumnsBlock.svelte';
	import LogoSliderBlockShell from '$components/Block/LogoSlider/LogoSliderBlockShell.svelte';
	import NewsletterBlock from '$components/Block/NewsletterBlock.svelte';
	import OrderConfiguratorBlock from '$components/Block/OrderConfigurator/OrderConfiguratorBlock.svelte';
	import PanoramaBlock from '$components/Block/PanoramaBlock.svelte';
	import PlansBlock from '$components/Block/PlansBlock.svelte';
	import PromotionBlock from '$components/Block/PromotionBlock.svelte';
	import QuotesBlock from '$components/Block/QuotesBlock.svelte';
	import ReviewsBlock from '$components/Block/Reviews/ReviewsBlock.svelte';
	import ShopItemCarouselBlock from '$components/Block/ShopItemCarousel.svelte';
	import TimelineBlock from '$components/Block/TimelineBlock.svelte';
	import USPBlock from '$components/Block/USPBlock.svelte';
	import Button from '$components/UI/Buttons/Button.svelte';
	import { sanitizeHtml } from '$lib/utils';
	import { isContentBlock } from '$models/Block/ContentBlock';

	interface Props {
		page: Page;
		block: Block;
		blockIndex?: number;
	}

	let { page, block, blockIndex = 0 }: Props = $props();

	const deferredBlockShells: Record<string, Component<any>> = {
		logo_slider: LogoSliderBlockShell
	};

	const deferredBlockLoaders: Record<string, () => Promise<{ default: Component<any> }>> = {
		logo_slider: () => import('$components/Block/LogoSlider/LogoSliderBlock.svelte')
	};

	const blockComponents: Record<string, Component<any>> = {
		cards: CardsBlock,
		timeline: TimelineBlock,
		faq: FaqBlock,
		form: FormBlock,
		newsletter: NewsletterBlock,
		plans: PlansBlock,
		promotions: PromotionBlock,
		featured: FeaturedBlock,
		columns: ColumnsBlock,
		list_columns: ListColumnsBlock,
		grid: GridBlock,
		order_configurator: OrderConfiguratorBlock,
		content_carousel: ContentCarouselBlock,
		shop_item_carousel: ShopItemCarouselBlock,
		reviews: ReviewsBlock,
		usps: USPBlock,
		quotes: QuotesBlock
	};

	const blocksWithoutIntro = [
		'featured',
		'faq',
		'content',
		'content_carousel',
		'newsletter',
		'promotions',
		'reviews',
		'shop_item_carousel',
		'usps',
		'quotes'
	];

	const blocksWithOwnVariant = ['quotes'];

	function getBlockActivationMode(type: string, index: number): 'immediate' | 'visible' {
		if (type in deferredBlockLoaders && index > 0) {
			return 'visible';
		}

		return 'immediate';
	}

	function resolveBlockComponent(type: string): Component<any> | null {
		return blockComponents[type] ?? null;
	}

	function resolveDeferredBlockShell(type: string): Component<any> | null {
		return deferredBlockShells[type] ?? null;
	}

	function resolveDeferredBlockLoader(type: string) {
		return deferredBlockLoaders[type] ?? null;
	}

	let blockComponent = $derived(resolveBlockComponent(block.type));
	let deferredBlockShell = $derived(resolveDeferredBlockShell(block.type));
	let deferredBlockLoader = $derived(resolveDeferredBlockLoader(block.type));
	let activationMode = $derived(getBlockActivationMode(block.type, blockIndex));
	let cssClasses = $derived.by(() => {
		const classes = new Set(['block', `block-${block.type.toLowerCase()}`]);

		if (isContentBlock(block) && block.image && block.imageIsWide) {
			classes.delete('block-content');
			classes.add('block-content-wide');
		}

		if (block.htmlClass) {
			classes.add(block.htmlClass);
		}

		if (block.variant && !blocksWithOwnVariant.includes(block.type)) {
			classes.add(`variant-${block.variant}`);
		}

		return [...classes];
	});

	function asPanoramaBlock(block: Block): PanoramaBlockType {
		return block as PanoramaBlockType;
	}

	function asContentBlock(block: Block): ContentBlockType {
		return block as ContentBlockType;
	}

	function asCallToActionBlock(block: Block): CallToActionBlockType {
		return block as unknown as CallToActionBlockType;
	}

	function getRenderedBlockKey(): string {
		return `${page.id}:${block.htmlId ?? block.type}:${blockIndex}`;
	}
</script>

<div class={cssClasses.join(' ')} style:background-color={block.variantBackgroundColor}>
	{#if block.htmlId}
		<div class="block-anchor" id={block.htmlId}></div>
	{/if}
	<div class="block-container container">
		{#if !blocksWithoutIntro.includes(block.type)}
			{#if block.title || block.subtitle}
				<div class="block-header block-header--align-{block.titleAlignment}">
					{#if block.subtitle}
						<div class="block-subtitle">{@html block.subtitle}</div>
					{/if}
					{#if block.title}
						<h2 class={['block-title', block.type === 'logo_slider' && 'block-title--small']}>
							{@html block.title}
						</h2>
					{/if}
				</div>
			{/if}
		{/if}

		{#key getRenderedBlockKey()}
			{#if block.type === 'panorama'}
				<PanoramaBlock block={asPanoramaBlock(block)} isPriority={blockIndex === 0} />
			{:else if block.type === 'content'}
				<ContentBlock block={asContentBlock(block)} />
			{:else if block.type === 'cta'}
				<CallToActionBlock block={asCallToActionBlock(block)} />
			{:else if activationMode === 'visible' && deferredBlockShell && deferredBlockLoader}
				<DeferredBlock
					{block}
					{activationMode}
					shell={deferredBlockShell}
					loadComponent={deferredBlockLoader}
				/>
			{:else if deferredBlockLoader}
				<ImmediateBlock {block} loadComponent={deferredBlockLoader} />
			{:else if blockComponent}
				{@const BlockComponent = blockComponent}
				<BlockComponent {block} />
			{/if}
		{/key}

		<!-- Block outro is not present on every block, so a block-specific conditional is not necessary here unless migrating away from outro fields -->
		{#if block.type != 'plans'}
			{#if block.outro && (block.outro.text || block.outro.button)}
				<div class="block-outro">
					{#if block.outro.text}
						<div class="block-outro-text">
							{@html sanitizeHtml(block.outro.text)}
						</div>
					{/if}

					{#if block.outro.button}
						<div class="block-outro-button">
							<Button type="anchor" href={block.outro.button.url} variant={block.outro.buttonClass}>
								{block.outro.button.title}
							</Button>
						</div>
					{/if}
				</div>
			{/if}
		{/if}
	</div>
</div>
